import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Panel, Btn, StatusBadge } from "@/admin/ui";

const JobInfo = ({ job }) => {
  if (!job) return null;
  return (
    <div className="mt-1 text-xs text-muted-foreground">
      <StatusBadge s={job.status === "done" ? "done" : job.status === "failed" ? "failed" : "running"} />{" "}
      {job.status === "done" && `${job.rule_codes.length} draft rules: ${job.rule_codes.slice(0, 6).join(", ")}${job.rule_codes.length > 6 ? "…" : ""}`}
      {job.status === "running" && "AI drafting in progress…"}
      {job.status === "failed" && job.error}
      {!!job.notes?.length && (
        <details className="mt-1"><summary className="cursor-pointer">AI notes ({job.notes.length})</summary>
          <ul className="mt-1 list-disc pl-5">{job.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
        </details>
      )}
    </div>
  );
};

export default function Sections({ docId }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => docId && api.get(`/admin/documents/${docId}/sections`).then((r) => setRows(r.data)), [docId]);
  useEffect(() => { load(); }, [load]);
  const running = rows.some((r) => r.job?.status === "running");
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running, load]);
  const draft = async (h) => {
    try { await api.post("/admin/ai/draft-rules", { document_id: docId, heading_passage_id: h.passage_id }); toast.success("AI drafting started"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!docId) return <Panel title="Sections"><p className="text-sm text-muted-foreground">Select a document above to see its sections.</p></Panel>;
  return (
    <Panel title="Sections — AI-assisted rule drafting" testId="sections-panel">
      <p className="mb-3 text-xs text-muted-foreground">AI reads one section, drafts rules in its own words with links to the source passages, and suggests passage classifications. Every drafted rule stays a draft until you approve it. Priority is set from the documented hierarchy, not by AI.</p>
      <ul className="max-h-[32rem] divide-y divide-border overflow-y-auto text-sm">
        {rows.filter((r) => r.words > 20).map((h) => (
          <li key={h.passage_id} className="py-2" style={{ paddingLeft: `${Math.max(0, h.level - 1) * 16}px` }} data-testid={`section-${h.passage_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span><strong>{h.text}</strong> <span className="text-xs text-muted-foreground">· {h.words} words</span></span>
              <Btn variant="ghost" disabled={h.job?.status === "running" || h.words > 12000} onClick={() => draft(h)} className="!px-3 !py-1 text-xs" data-testid={`draft-section-${h.passage_id}`}>
                <Sparkles className="h-3.5 w-3.5" /> {h.job ? "Re-draft" : "Draft rules"}
              </Btn>
            </div>
            <JobInfo job={h.job} />
            {h.drafted_within && <p className="mt-1 text-xs text-amber-300" data-testid={`section-parent-warning-${h.passage_id}`}>Already covered by the drafted section "{h.drafted_within}" — re-drafting here may create overlapping rules.</p>}
          </li>
        ))}
      </ul>
    </Panel>
  );
}
